import React, { useState } from "react";
import { Box, Typography, Collapse, Skeleton, IconButton } from "@mui/material";
import { ChevronDown, ChevronUp, Bot } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import Spinner from "components/common/Spinner";

interface TaskAgentContainerProps {
  title: React.ReactNode;
  children: React.ReactNode;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  isProcessing?: boolean;
  processingSteps?: string[];
  secondaryActions?: React.ReactNode;
  showProcessingAfterContent?: boolean;
}

const ProcessingDisplay = ({
  steps = [],
  currentStep = 0,
}: {
  steps: string[];
  currentStep?: number;
}) => {
  return (
    <Box display="flex" flexDirection="column" gap="24px">
      <Box display="flex" flexDirection="column" gap="8px">
        {steps.map((step, index) => (
          <Typography
            key={index}
            variant="body"
            color={index <= currentStep ? undefined : "text.tertiary"}
          >
            {step}
          </Typography>
        ))}
      </Box>
      <Box display="flex" flexDirection="row" gap="18px">
        <Skeleton
          variant="rounded"
          width={92}
          height={92}
          sx={{ borderRadius: "8px" }}
        />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          gap="8px"
          flex="1"
        >
          <Skeleton variant="rounded" width="50%" height={20} />
          <Skeleton variant="rounded" width="74%" height={8} />
          <Skeleton variant="rounded" width="60%" height={8} />
          <Skeleton variant="rounded" width="70%" height={8} />
        </Box>
      </Box>
    </Box>
  );
};

export default function TaskAgentContainer({
  title,
  children,
  isCollapsed = false,
  onToggleCollapsed,
  isProcessing = false,
  processingSteps = [],
  secondaryActions,
  showProcessingAfterContent = false,
}: TaskAgentContainerProps) {
  const isMobile = useIsMobile();
  const [internalCollapsed, setInternalCollapsed] = useState(isCollapsed);

  const collapsed = onToggleCollapsed ? isCollapsed : internalCollapsed;
  const toggleCollapsed =
    onToggleCollapsed || (() => setInternalCollapsed(!internalCollapsed));

  const processingTitle = (
    <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
      <Spinner />
      <Typography color="#3D3D3D" variant="bodyHeavy">
        {typeof title === "string" ? title : "Processing..."}
      </Typography>
    </Box>
  );

  const normalTitle = (
    <Box
      display="flex"
      flexDirection="row"
      gap="10px"
      sx={{ alignItems: "center" }}
    >
      <Bot size={20} color="#198282" />
      <Typography color="#3D3D3D" variant="bodyHeavy">
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "16px",
        padding: isMobile ? "20px" : "16px 24px",
        background: "#FFF",
        border: "1px solid #e0e0e0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {isProcessing ? processingTitle : normalTitle}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {!collapsed && secondaryActions && secondaryActions}
          <IconButton
            onClick={toggleCollapsed}
            sx={{
              padding: "4px",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            {collapsed ? (
              <ChevronDown size={24} stroke="#8E9598" />
            ) : (
              <ChevronUp size={24} stroke="#8E9598" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={!collapsed}>
        <Box
          sx={{
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {showProcessingAfterContent ? (
            <>
              {children}
              {isProcessing && processingSteps.length > 0 && (
                <ProcessingDisplay steps={processingSteps} />
              )}
            </>
          ) : isProcessing ? (
            <ProcessingDisplay steps={processingSteps} />
          ) : (
            children
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
